import { create } from 'zustand';
import { mmkv } from '@/storage/mmkv';
import type { DeploymentInfo, DeploymentConfig } from '@/types/deployment';
import { AppApiClient } from '@/api/client';

// Build a DeploymentConfig from EXPO_PUBLIC_* env vars (.env) when present.
// Lets a deployed stack be configured via .env instead of in-app setConfig.
function configFromEnv(): DeploymentConfig | null {
  const apiBaseUrl = process.env.EXPO_PUBLIC_API_URL;
  if (!apiBaseUrl) return null;
  const siteBaseUrl = process.env.EXPO_PUBLIC_SITE_BASE_URL;
  return {
    mode: process.env.EXPO_PUBLIC_BACKEND_MODE === 'mock' ? 'mock' : 'aws',
    apiBaseUrl: apiBaseUrl.replace(/\/+$/, ''),
    siteBaseUrl: siteBaseUrl ? siteBaseUrl.replace(/\/+$/, '') : undefined,
    region: process.env.EXPO_PUBLIC_AWS_REGION || 'us-west-1',
    userPoolId: process.env.EXPO_PUBLIC_COGNITO_USER_POOL_ID || '',
    userPoolClientId: process.env.EXPO_PUBLIC_COGNITO_CLIENT_ID || '',
    bucketName: process.env.EXPO_PUBLIC_STORAGE_BUCKET || '',
  };
}

interface DeploymentState {
  deployments: Record<string, DeploymentInfo>;
  config: DeploymentConfig | null;

  loadConfig: () => void;
  setConfig: (config: DeploymentConfig) => void;
  getDeployment: (projectId: string) => DeploymentInfo;
  setDeployment: (projectId: string, info: DeploymentInfo) => void;
  createClient: (projectId: string) => AppApiClient | null;
  publishProject: (project: import('@/storage/ProjectRepository').Project, opts: { icon?: string; category?: string }) => Promise<void>;
}

export const useDeploymentStore = create<DeploymentState>((set, get) => ({
  deployments: {},
  config: null,

  loadConfig: () => {
    const configJson = mmkv.getString('deployment_config');
    if (configJson) {
      set({ config: JSON.parse(configJson) as DeploymentConfig });
      return;
    }
    // No persisted config — fall back to .env (EXPO_PUBLIC_*) if provided.
    const envConfig = configFromEnv();
    if (envConfig) set({ config: envConfig });
  },

  setConfig: (config) => {
    set({ config });
    mmkv.setString('deployment_config', JSON.stringify(config));
  },

  getDeployment: (projectId) => {
    return get().deployments[projectId] ?? { status: 'local' };
  },

  setDeployment: (projectId, info) => {
    set((state) => ({
      deployments: { ...state.deployments, [projectId]: info },
    }));
    mmkv.setString(`deployment_${projectId}`, JSON.stringify(info));
  },

  createClient: (projectId) => {
    const { config, deployments } = get();
    const deployment = deployments[projectId];
    if (!config || !deployment?.apiUrl || !deployment.appId) return null;

    return new AppApiClient({
      baseUrl: deployment.apiUrl,
      appId: deployment.appId,
    });
  },

  publishProject: async (project, opts) => {
    const { buildPublishInput } = await import('@/utils/publishInput');
    const { nextStatus } = await import('./deploymentStatus');
    const { useBackendStore } = await import('@/store/backendStore');

    get().setDeployment(project.id, nextStatus(get().getDeployment(project.id), { type: 'start' }));
    try {
      const listing = await useBackendStore.getState().platform.publishApp(buildPublishInput(project, opts));
      get().setDeployment(project.id, nextStatus(get().getDeployment(project.id), { type: 'success', appId: listing.appId }));
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Publish failed';
      get().setDeployment(project.id, nextStatus(get().getDeployment(project.id), { type: 'error', message }));
      throw e;
    }
  },
}));

// Auto-load config on store creation
useDeploymentStore.getState().loadConfig();
